import React from 'react';

import { EMASatisfactionType } from '@demind-inc/core';
import { ReactComponent as Neutral } from '../../assets/ema/neutral.svg';
import { ReactComponent as NotAtAll } from '../../assets/ema/not_at_all.svg';
import { ReactComponent as Slightly } from '../../assets/ema/slightly.svg';
import { ReactComponent as Very } from '../../assets/ema/very.svg';
import { ReactComponent as Extremely } from '../../assets/ema/extremely.svg';
import { DataMenuItem } from '../../data-access';
import { getCssVariable } from '../../utils';
import { EventDataTrendWithData } from '@demind-inc/core';
import { CustomTooltipContentProps } from '../common';
import { circadianTooltipInfo } from '../Energy';

interface DataViewMenuItem {
  id: DataMenuItem;
  title: string;
  explanation?: CustomTooltipContentProps;
}

interface EventItemData {
  eventKey: keyof EventDataTrendWithData;
  label: string;
  iconType: 'stress' | 'energy';
}

const sleepExplanationInfo: CustomTooltipContentProps = {
  title: '💤 Sleep Quality',
  description:
    'Sleep quality is how restorative your sleep is, not just how long you stayed in bed. Duration and consistency both matter. Lifestack uses these sleep signals when computing your Energy Score so your schedule better matches your real capacity.',
  referenceLinkList: [
    {
      title: 'Measuring Sleep Efficiency: What Should the Denominator Be?',
      url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4751425/',
    },
    {
      title: "National Sleep Foundation's sleep quality recommendations: first report",
      url: 'https://www.sciencedirect.com/science/article/abs/pii/S2352721816301309',
    },
    {
      title:
        'Irregular sleep/wake patterns are associated with poorer academic performance and delayed circadian and sleep/wake timing',
      url: 'https://www.nature.com/articles/s41598-017-03171-4',
    },
    {
      title:
        'Sleep quality, duration, and consistency are associated with better academic performance in college students',
      url: 'https://www.nature.com/articles/s41539-019-0055-z',
    },
    {
      title:
        'Sleep regularity is a stronger predictor of mortality risk than sleep duration: A prospective cohort study',
      url: 'https://academic.oup.com/sleep/article/47/1/zsad253/7280269?login=false#supplementary-data',
    },
    {
      title:
        'Recommended Amount of Sleep for a Healthy Adult: A Joint Consensus Statement of the American Academy of Sleep Medicine and Sleep Research Society',
      url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4434546/',
    },
  ],
};

export const DataViewMenuItems: DataViewMenuItem[] = [
  {
    id: 'dataCircadian',
    title: 'Circadian Rhythm',
    explanation: circadianTooltipInfo,
  },
  {
    id: 'dataTask',
    title: 'Task Completion',
  },
  {
    id: 'dataSleep',
    title: 'Sleep',
    explanation: sleepExplanationInfo,
  },
  {
    id: 'dataTrend',
    title: 'Most Energizing Event',
  },
];

export const colorBlueBase = getCssVariable('--color-blue-base');
export const colorGreenBase = getCssVariable('--color-green-base');
export const colorOrangeBase = getCssVariable('--color-orange-base');
export const colorRedBase = getCssVariable('--color-red-base');
export const colorYellowBase = getCssVariable('--color-yellow-base');
export const colorBlueLight = getCssVariable('--color-blue-light');
export const grayPurple = getCssVariable('--color-gray-purple');
export const grayBase = getCssVariable('--color-gray-base');
export const purpleTaro = getCssVariable('--color-purple-taro');
export const primaryColor = getCssVariable('--color-primary');
export const grayLight = getCssVariable('--color-gray-light');
export const grayLightest = getCssVariable('--color-gray-lightest');
export const white = getCssVariable('--color-white-lightest');
export const grayDark = getCssVariable('--color-gray-dark');

export const CHART_HEIGHT = 160;

export const sleepSummaryColor = {
  deep: colorBlueBase,
  light: colorBlueLight,
  rem: colorOrangeBase,
  awake: colorGreenBase,
};

export const sleepSummaryText = {
  deep: 'Deep',
  light: 'Light',
  rem: 'REM',
  awake: 'Awake',
};

export const eventTypeInfo: EventItemData[] = [
  {
    eventKey: 'energyBoostingEvent',
    label: 'Most energizing event',
    iconType: 'energy',
  },
  {
    eventKey: 'energyDrainingEvent',
    label: 'Most draining event',
    iconType: 'energy',
  },
  {
    eventKey: 'stressfulEvent',
    label: 'Most stressful event',
    iconType: 'stress',
  },
];

export const satisfactionColor: Record<string, string> = {
  not_at_all: getCssVariable('--color-red-base'),
  slightly: getCssVariable('--color-orange-base'),
  neutral: getCssVariable('--color-yellow-base'),
  very: getCssVariable('--color-green-base'),
  extremely: getCssVariable('--color-green-base'),
  unanswered: getCssVariable('--color-yellow-base'),
};

export const getSatisfactionIcon = (
  type: EMASatisfactionType | 'unanswered',
  colorScheme: 'selected' | 'unselected' = 'selected',
  size = 20,
  className = ''
) => {
  switch (type) {
    case 'not_at_all':
      return (
        <NotAtAll
          style={{
            width: size,
            height: size,
            opacity: colorScheme === 'selected' ? 1 : 0.3,
          }}
          className={className}
        />
      );
    case 'slightly':
      return (
        <Slightly
          style={{ width: size, height: size, opacity: colorScheme === 'selected' ? 1 : 0.3 }}
          className={className}
        />
      );
    case 'neutral':
      return (
        <Neutral
          style={{ width: size, height: size, opacity: colorScheme === 'selected' ? 1 : 0.3 }}
          className={className}
        />
      );
    case 'unanswered':
      return (
        <Neutral
          style={{ width: size, height: size, opacity: colorScheme === 'selected' ? 1 : 0.3 }}
          className={className}
        />
      );
    case 'very':
      return (
        <Very
          style={{ width: size, height: size, opacity: colorScheme === 'selected' ? 1 : 0.3 }}
          className={className}
        />
      );
    case 'extremely':
      return (
        <Extremely
          style={{ width: size, height: size, opacity: colorScheme === 'selected' ? 1 : 0.3 }}
          className={className}
        />
      );
  }
};

export const getSatisfactionText = (type: EMASatisfactionType | 'unanswered') => {
  switch (type) {
    case 'not_at_all':
      return 'Very unsatisfied';
    case 'slightly':
      return 'Unsatisfied';
    case 'neutral':
      return 'Neutral';
    case 'very':
      return 'Satisfied';
    case 'extremely':
      return 'Very satisfied';
    case 'unanswered':
      return '--';
  }
};

export const satisfactionOptions: EMASatisfactionType[] = [
  'extremely',
  'very',
  'neutral',
  'not_at_all',
  'slightly',
];
export const subjectiveFeelingsTooltipInfo: CustomTooltipContentProps = {
  title: 'Subjective Energy',
  description: `Wearables provide useful objective signals, but they do not always match how you feel. Your self-reported energy is important too. When you report how energized you feel, we make a short-term adjustment to your energy curve.

After an event, we may ask: "How energized do you feel right now?" We use your answer, event duration, and category to adjust your energy.

For each energy zone (for example, Morning Peak or Warm-up), we may ask how energized you feel. You can also tap the zone label to report how you felt in that period.

Note: Turn on this feature in Settings to receive these check-in notifications.`,
  referenceLinkList: [
    {
      title:
        'Quantifying Energy and Fatigue: Classification and Assessment of Energy and Fatigue Using Subjective, Objective, and Mixed Methods towards Health and Quality of Life',
      url: 'https://link.springer.com/chapter/10.1007/978-3-030-94212-0_4',
    },
    {
      title:
        'micro-Stress EMA: A Passive Sensing Framework for Detecting in-the-wild Stress in Pregnant Mothers',
      url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7236910/',
    },
  ],
};

export const chronotypeTooltipInfo: CustomTooltipContentProps = {
  title: '🕝 Chronotype',
  description:
    'Chronotype is your natural daily alertness pattern. It reflects when your energy tends to rise and fall. Lifestack uses sleep data to estimate whether you are morning, hybrid, or night type, then personalizes task timing.',
  referenceLinkList: [
    {
      title: 'Chronotypes in the US – Influence of age and sex',
      url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5479630/',
    },
  ],
  learnMoreLink: 'https://demind-inc.notion.site/Chronotype-26f7c97113e6813b8cdbcc835020f6de',
};
